import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Zap, Calculator, Info, BookOpen } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './ElectricityCostCalculatorPage/components/CalculationForm';
import ResultsOverview from './ElectricityCostCalculatorPage/components/ResultsOverview';
import UsageBreakdown from './ElectricityCostCalculatorPage/components/UsageBreakdown';
import QuickCalculationSidebar from './ElectricityCostCalculatorPage/components/QuickCalculationSidebar';
import SavedCalculationsSidebar from './ElectricityCostCalculatorPage/components/SavedCalculationsSidebar';
import TipsSidebar from './ElectricityCostCalculatorPage/components/TipsSidebar';
import InfoContent from './ElectricityCostCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  region: 'moscow', // Регион России
  tariffType: 'single', // single, day_night, zones
  meterType: 'single', // single или multi (однотарифный или многотарифный)
  consumptionType: 'manual', // manual или appliances
  // Однотарифный учет
  totalConsumption: '250', // Общее потребление кВт⋅ч
  // Многотарифный учет
  dayConsumption: '180', // Дневное потребление кВт⋅ч
  nightConsumption: '70', // Ночное потребление кВт⋅ч
  peakConsumption: '100', // Пиковое потребление кВт⋅ч
  semipeakConsumption: '100', // Полупиковое потребление кВт⋅ч
  offpeakConsumption: '50', // Внепиковое потребление кВт⋅ч
  // Дата для расчета сезонных тарифов
  calculationDate: new Date().toISOString().split('T')[0],
  // Параметры для расчета бытовой техники
  appliances: [],
  // Льготы
  hasDiscount: false,
  discountType: 'social', // social, large_family, veteran, disabled
  discountPercent: '50',
  // Предыдущие показания для расчета прироста
  hasPreviousReadings: false,
  previousReading: '5750',
  currentReading: '6000',
  // Дополнительные параметры
  includeExtraFees: false,
  householdMembers: '1',
  householdArea: '50',
  socialNorm: '500', // кВт⋅ч в месяц
};

// Российские регионы с тарифами (примерные данные на 2023 год)
export const russianRegions = [
  { id: 'moscow', name: 'Москва', singleRate: 6.43, dayRate: 7.40, nightRate: 2.13, peakRate: 7.70, semipeakRate: 6.43, offpeakRate: 2.13 },
  { id: 'moscow_region', name: 'Московская область', singleRate: 6.29, dayRate: 7.23, nightRate: 2.52, peakRate: 7.48, semipeakRate: 6.29, offpeakRate: 2.52 },
  { id: 'saint_petersburg', name: 'Санкт-Петербург', singleRate: 5.13, dayRate: 5.84, nightRate: 3.38, peakRate: 5.95, semipeakRate: 5.13, offpeakRate: 3.38 },
  { id: 'krasnodar', name: 'Краснодарский край', singleRate: 5.24, dayRate: 6.02, nightRate: 3.71, peakRate: 6.14, semipeakRate: 5.24, offpeakRate: 3.71 },
  { id: 'novosibirsk', name: 'Новосибирская область', singleRate: 4.25, dayRate: 5.12, nightRate: 3.00, peakRate: 5.30, semipeakRate: 4.25, offpeakRate: 3.00 },
  { id: 'ekaterinburg', name: 'Свердловская область', singleRate: 4.50, dayRate: 5.17, nightRate: 2.42, peakRate: 5.40, semipeakRate: 4.50, offpeakRate: 2.42 },
  { id: 'kazan', name: 'Республика Татарстан', singleRate: 4.20, dayRate: 4.83, nightRate: 2.52, peakRate: 5.04, semipeakRate: 4.20, offpeakRate: 2.52 },
  { id: 'vladivostok', name: 'Приморский край', singleRate: 5.67, dayRate: 6.52, nightRate: 3.97, peakRate: 6.80, semipeakRate: 5.67, offpeakRate: 3.97 }
];

// Типы тарифов
export const tariffTypes = [
  { id: 'single', name: 'Одноставочный тариф', description: 'Единая стоимость электроэнергии, независимо от времени суток.' },
  { id: 'day_night', name: 'День/Ночь (двухзонный)', description: 'Стоимость зависит от времени: день (7:00-23:00) и ночь (23:00-7:00).' },
  { id: 'zones', name: 'Пиковая зона (трехзонный)', description: 'Пиковая (утро/вечер), полупиковая (день) и ночная зоны.' }
];

// Типы счетчиков
export const meterTypes = [
  { id: 'single', name: 'Однотарифный счетчик', description: 'Общий учет для всех периодов времени.' },
  { id: 'multi', name: 'Многотарифный счетчик', description: 'Раздельный учет по времени суток.' }
];

// Типы льгот
export const discountTypes = [
  { id: 'social', name: 'Социальная льгота', percent: 50, description: 'Для малоимущих граждан.' },
  { id: 'large_family', name: 'Многодетная семья', percent: 30, description: 'Для семей с тремя и более детьми.' },
  { id: 'veteran', name: 'Ветераны труда/войны', percent: 50, description: 'Для ветеранов ВОВ и труда.' },
  { id: 'disabled', name: 'Инвалиды', percent: 50, description: 'Для лиц с ограниченными возможностями.' }
];

// Список общих бытовых приборов
export const commonAppliances = [
  { id: 'fridge', name: 'Холодильник', power: 150, hoursPerDay: 8, usageFactor: 1.0 },
  { id: 'washing_machine', name: 'Стиральная машина', power: 2000, hoursPerDay: 1, usageFactor: 0.2 },
  { id: 'dishwasher', name: 'Посудомоечная машина', power: 1500, hoursPerDay: 1, usageFactor: 0.15 },
  { id: 'tv', name: 'Телевизор', power: 120, hoursPerDay: 4, usageFactor: 1.0 },
  { id: 'computer', name: 'Компьютер/Ноутбук', power: 200, hoursPerDay: 4, usageFactor: 1.0 },
  { id: 'microwave', name: 'Микроволновая печь', power: 1000, hoursPerDay: 0.5, usageFactor: 1.0 },
  { id: 'electric_oven', name: 'Электрическая духовка', power: 2000, hoursPerDay: 0.5, usageFactor: 1.0 },
  { id: 'kettle', name: 'Электрический чайник', power: 2000, hoursPerDay: 0.5, usageFactor: 1.0 },
  { id: 'air_conditioner', name: 'Кондиционер', power: 1000, hoursPerDay: 3, usageFactor: 0.8 },
  { id: 'electric_heater', name: 'Электрообогреватель', power: 2000, hoursPerDay: 4, usageFactor: 0.8 },
  { id: 'water_heater', name: 'Водонагреватель', power: 2000, hoursPerDay: 2, usageFactor: 0.7 },
  { id: 'light_bulbs', name: 'Освещение (все лампы)', power: 200, hoursPerDay: 5, usageFactor: 1.0 },
  { id: 'iron', name: 'Утюг', power: 2000, hoursPerDay: 0.5, usageFactor: 0.15 },
  { id: 'vacuum_cleaner', name: 'Пылесос', power: 1500, hoursPerDay: 0.5, usageFactor: 0.15 },
  { id: 'hair_dryer', name: 'Фен', power: 1500, hoursPerDay: 0.1, usageFactor: 0.1 }
];

/**
 * ElectricityCostCalculatorPage Component - Калькулятор расчета стоимости электроэнергии с различными тарифами и параметрами.
 */
const ElectricityCostCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('electricity-cost-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор стоимости электроэнергии | Gensio';
  const metaDescription = tool?.metaDescription || 'Расчет стоимости электроэнергии онлайн: однотарифный и многотарифный учет, расчет по бытовым приборам и разным тарифным зонам с учетом льгот и региональных тарифов.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);
  const [appliancesList, setAppliancesList] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных расчетов из localStorage ---
  useEffect(() => {
    const savedCalcsData = localStorage.getItem('savedElectricityCalculations');
    if (savedCalcsData) {
      try {
        setSavedCalculations(JSON.parse(savedCalcsData));
      } catch (e) {
        console.error("Error loading saved calculations:", e);
      }
    }

    // Инициализация списка приборов
    setAppliancesList(commonAppliances.map(appliance => ({
      ...appliance,
      active: false,
      quantity: 1
    })));
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'single-tariff':
          newState.tariffType = 'single';
          newState.meterType = 'single';
          break;
        case 'day-night':
          newState.tariffType = 'day_night';
          newState.meterType = 'multi';
          break;
        case 'three-zones':
          newState.tariffType = 'zones';
          newState.meterType = 'multi';
          break;
        case 'appliances':
          newState.consumptionType = 'appliances';
          // Активируем некоторые приборы по умолчанию
          setAppliancesList(prev => 
            prev.map(appliance => ({
              ...appliance,
              active: ['fridge', 'tv', 'light_bulbs'].includes(appliance.id)
            }))
          );
          break;
        case 'with-discount':
          newState.hasDiscount = true;
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setErrors({});

  }, [subToolId]);

  // Сохранение расчета в localStorage
  const saveCalculation = (name) => {
    if (!results) return;

    const newSavedCalc = {
      id: Date.now().toString(),
      name: name || `Расчет для ${russianRegions.find(r => r.id === formData.region)?.name || 'региона'}`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results }
    };

    const updatedSavedCalcs = [...savedCalculations, newSavedCalc];
    setSavedCalculations(updatedSavedCalcs);
    localStorage.setItem('savedElectricityCalculations', JSON.stringify(updatedSavedCalcs));

    return newSavedCalc;
  };

  // Удаление сохраненного расчета
  const deleteSavedCalculation = (id) => {
    const updatedSavedCalcs = savedCalculations.filter(calc => calc.id !== id);
    setSavedCalculations(updatedSavedCalcs);
    localStorage.setItem('savedElectricityCalculations', JSON.stringify(updatedSavedCalcs));
  };

  // Загрузка сохраненного расчета
  const loadSavedCalculation = (id) => {
    const calcToLoad = savedCalculations.find(calc => calc.id === id);
    if (calcToLoad) {
      setFormData(calcToLoad.formData);
      setResults(calcToLoad.results);
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Zap className="w-8 h-8 text-yellow-500 dark:text-yellow-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор стоимости электроэнергии'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              russianRegions={russianRegions}
              tariffTypes={tariffTypes}
              meterTypes={meterTypes}
              discountTypes={discountTypes}
              appliancesList={appliancesList}
              setAppliancesList={setAppliancesList}
              saveCalculation={saveCalculation}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} formData={formData} russianRegions={russianRegions} />
                <UsageBreakdown 
                  results={results} 
                  formData={formData}
                  appliancesList={appliancesList}
                />
              </>
            )}

            {/* --- Информационный контент об электроэнергии --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрого расчета --- */}
            <QuickCalculationSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              russianRegions={russianRegions}
            />

            {/* --- Панель сохраненных расчетов --- */}
            <SavedCalculationsSidebar 
              savedCalculations={savedCalculations}
              deleteSavedCalculation={deleteSavedCalculation}
              loadSavedCalculation={loadSavedCalculation}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-yellow-500 focus:ring-yellow-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-yellow-500 focus:ring-yellow-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Energy Usage Chart */
        .energy-chart .bar {
          transition: width 0.6s ease, background-color 0.3s ease;
        }
        .energy-chart .bar-label {
          transition: opacity 0.3s ease;
        }
      `}</style>
    </div>
  );
};

export default ElectricityCostCalculatorPage;